/**
 * Etat du catalogue sur le poste : nombre d'articles, date de la derniere
 * confirmation par le serveur, derniere erreur de synchro (on garde alors
 * l'ancien catalogue, et on le dit), barre de progression et bouton de
 * mise a jour sur demande.
 */
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { palette } from './theme';

const dateTimeFormat = new Intl.DateTimeFormat('fr-CA', {
    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Toronto',
});

export function formatDateTime(iso) {
    return dateTimeFormat.format(new Date(iso));
}

const STEP_LABELS = {
    config: 'Préparation…',
    download: 'Téléchargement du catalogue…',
    verify: 'Vérification de la signature…',
    save: 'Enregistrement sur le poste…',
    roster: 'Liste des employés…',
    done: 'Terminé',
};

const ERROR_REASONS = {
    HORS_LIGNE: 'Pas de connexion à l\'ERP.',
    TELECHARGEMENT: 'Le téléchargement a été coupé.',
    SIGNATURE: 'La signature du catalogue reçu est invalide.',
    CATALOGUE_INVALIDE: 'Le catalogue reçu est incomplet.',
    CATALOGUE_NON_SIGNE: 'L\'ERP ne peut pas signer le catalogue. Prévenez l\'administrateur de l\'ERP.',
    CLE_ABSENTE: 'L\'ERP ne peut pas signer le catalogue. Prévenez l\'administrateur de l\'ERP.',
    TROP_DE_DEMANDES: 'Trop de demandes : réessayez dans quelques minutes.',
};

/** Raison lisible d'un code d'erreur de synchro. */
export function syncErrorReason(code) {
    return ERROR_REASONS[code] || 'Réessayez plus tard.';
}

export default function CatalogStatus({ catalogMeta, catalogSync, syncing, progress, onSync, canSync = true }) {
    const error = catalogSync && catalogSync.last_error;
    return (
        <Box data-testid="etat-catalogue" sx={{ width: '100%', maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography component="h2" sx={{ fontSize: 22, fontWeight: 800, color: palette.text }}>Catalogue</Typography>
            {catalogMeta ? (
                <Typography data-testid="catalogue-resume" sx={{ fontSize: 18 }}>
                    {catalogMeta.item_count} articles · prix confirmés le {formatDateTime(catalogMeta.confirmed_at)}
                </Typography>
            ) : (
                <Typography data-testid="catalogue-resume" sx={{ fontSize: 18 }}>Aucun catalogue sur ce poste.</Typography>
            )}
            {syncing && progress && (
                <Box>
                    <LinearProgress
                        variant="determinate"
                        value={progress.percent}
                        aria-label="Synchro du catalogue"
                        sx={{ height: 12, borderRadius: 6 }}
                    />
                    <Typography sx={{ fontSize: 16, color: palette.textSecondary, mt: 0.5 }}>
                        {STEP_LABELS[progress.step] || 'Synchro…'}
                    </Typography>
                </Box>
            )}
            {!syncing && error && (
                <Alert severity="warning" data-testid="catalogue-erreur" sx={{ fontSize: 17 }}>
                    {catalogMeta
                        ? `Catalogue non mis à jour. ${syncErrorReason(error)} Le catalogue du ${formatDateTime(catalogMeta.confirmed_at)} reste utilisé.`
                        : `Catalogue non chargé. ${syncErrorReason(error)}`}
                </Alert>
            )}
            {onSync && (
                <Button variant="outlined" onClick={onSync} disabled={syncing || !canSync} sx={{ alignSelf: 'flex-start', minHeight: 64 }}>
                    {syncing ? 'Mise à jour…' : 'Mettre à jour le catalogue'}
                </Button>
            )}
        </Box>
    );
}
